import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';

// COMPONENTS
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';

// SERVICES
import { getDefaultOptionDrop, getTranslateByKey } from '@/main/services/utils';
import { KeyValue } from '@/main/models/common';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';

// REDUX
import { getStatusRegister, useStatusRegisterReducer } from '@/main/store/statusRegister';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import { getSupplierCategory, useSupplierCategoryReducer } from '@/main/store/supplierCategory';

import { useSupplierRegisterContext } from '../context';
import * as S from './styles';
import BodyshopAssignationModal from '../bodyshopAssignationModal';

interface ISectionInfoProps {
  dataAssignableBodyshop: KeyValue[];
  handleBodyshopAssignment: (value: string | null) => void;
  guidAssignedBodyShop: string | null;
  watcherCountry: KeyValue | null;
  watcherRfc: string | null;
  loadBodyshopByRFC: (rfc: string) => void;
  setGuidAssignedBodyShop: (value: string | null) => void;
}


function SectionInfo({
  dataAssignableBodyshop,
  handleBodyshopAssignment,
  guidAssignedBodyShop,
  watcherCountry,
  watcherRfc,
  loadBodyshopByRFC,
  setGuidAssignedBodyShop
}: ISectionInfoProps) {
  const dispatch = useDispatch();
  const countryReducer = useCountryReducer();
  const statusRegisterReducer = useStatusRegisterReducer();
  const supplierCategoryReducer = useSupplierCategoryReducer();
  const { isEdit } = useSupplierRegisterContext();
  const {
    control,
    setValue,
    formState: { errors }
  } = useFormContext();
  const optionDefaultCountryDrop = getDefaultOptionDrop(
    countryReducer.countriesUser.data
  );
  const isDisabledCountry = () => isEdit || optionDefaultCountryDrop !== null;

  useEffect(() => {
    dispatch(getCountriesUser());
    dispatch(getStatusRegister());
    dispatch(getSupplierCategory());
  }, []);

  return (
    <S.Container id='register-sectionInfo'>
      <KogAutoCompleteControlled
        nameCustom="country"
        id="id-registerSupplier-input-country"
        label={getTranslateByKey('information.country')}
        control={control}
        disabled={isDisabledCountry()}
        rulesCustom={{ required: true }}
        errorMessageCustom={
          errors.country?.type === 'required' ? 'Country is required' : ''
        }
        hasErrorCustom={!!errors.country}
        options={countryReducer.countriesUser.data}
        isLoading={countryReducer.countriesUser.isLoading}
        onChangeCustom={(optionSelected: KeyValue | null) => {}}
        autoHighlightCustom={!!optionDefaultCountryDrop}
        setValueCustom={setValue}
      />
      <KogInputTextControlledNew
        name="companyName"
        id="id-registerSupplier-input-companyName"
        label={getTranslateByKey('information.companyName')}
        control={control}
        inputProps={{ maxLength: 100 }}
        controllerProps={{ rules: { required: true } }}
      />
      <KogInputTextControlledNew
        name="rfc"
        id="id-registerSupplier-input-rfc"
        label={getTranslateByKey('information.rfc')}
        control={control}
        disabled={!watcherCountry}
        inputProps={{ maxLength: 20 }}
        controllerProps={{ rules: { required: true, minLength: 12 } }}
        onChangeCustom={debounce(val => {
          loadBodyshopByRFC(val);
        }, 500)}
      />
      <KogInputTextControlledNew
        name="tradeName"
        id="id-registerSupplier-input-tradeName"
        label={getTranslateByKey('information.tradeName')}
        control={control}
        inputProps={{ maxLength: 100 }}
        controllerProps={{ rules: { required: true } }}
      />
      <KogAutoCompleteControlled
        nameCustom="supplierCategory"
        id="id-registerSupplier-input-supplierCategory"
        label={getTranslateByKey('information.type')}
        setValueCustom={setValue}
        options={supplierCategoryReducer.supplierCategory.data}
        isLoading={supplierCategoryReducer.supplierCategory.isLoading}
        control={control}
        rulesCustom={{ required: true }}
        errorMessageCustom={
          errors.supplierCategory?.type === 'required'
            ? getTranslateByKey('information.typeRequired')
            : ''
        }
        hasErrorCustom={!!errors.supplierCategory}
        onChangeCustom={() => {}}
      />
      <KogAutoCompleteControlled
        nameCustom="status"
        id="id-registerSupplier-input-status"
        label={getTranslateByKey('information.status')}
        valueDefaultKey={
          !isEdit ? String(StatusRegisterEnum.Active) : undefined
        }
        setValueCustom={setValue}
        options={statusRegisterReducer.statusRegister.data}
        isLoading={statusRegisterReducer.statusRegister.isLoading}
        control={control}
        rulesCustom={{ required: true }}
        errorMessageCustom={
          errors.status?.type === 'required'
            ? getTranslateByKey('information.statusRequired', true)
            : ''
        }
        hasErrorCustom={!!errors.status}
        onChangeCustom={() => {}}
      />
      <BodyshopAssignationModal
        dataAssignableBodyshop={dataAssignableBodyshop}
        setGuidAssignedBodyShop={setGuidAssignedBodyShop}
        handleBodyshopAssignment={handleBodyshopAssignment}
        open={dataAssignableBodyshop.length > 0 && !guidAssignedBodyShop && (!errors.rfc)}
        rfc={watcherRfc || ''}
      />
    </S.Container>
  );
}

export default SectionInfo;
